import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import {
  foyerApi,
  type IngestJobView,
  type SubmissionQueue,
  type SubmissionRow,
  type VodTitleRow,
} from "../http/foyer";
import type { Genre, Provider } from "@shared/types/content-api";
import { assetPlaybackBlockers } from "@shared/channel-playability";
import { availabilityFor } from "@shared/content-rights";
import { draftToUpdate } from "@shared/asset-draft";
import AssetOverviewTab from "../components/distribution/AssetOverviewTab";
import AssetEditorDialog from "../components/distribution/AssetEditorDialog";
import FeedReviewView from "../components/distribution/FeedReviewView";
import { friendlyError } from "../utils/apiError";
import { tokens } from "../theme";
import { ingestHoldExplanation } from "@shared/ingest-gate";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import RssFeedIcon from "@mui/icons-material/RssFeed";

/**
 * The review queue (`CONTENT-17`) — screen a supplier's upload before anybody
 * can see it.
 *
 * ## The list is built from OUR collections
 *
 * `content_submissions` first, upstream second. The catalogue has no submission
 * state, no owner filter and no sorting at all, so a page of it filtered to
 * submissions would be arbitrarily short with a `total` that is a lie — the
 * same class of bug as the portal's seasons transformer.
 *
 * ## Why the whole queue is loaded, and why that is the honest choice
 *
 * A review queue is bounded by definition: it is what humans have not got to
 * yet. Loading it whole means **every filter is honest**, including the three
 * facets that live upstream (type, language, genre) — filtering a PAGE by them
 * would silently narrow a window rather than the set. The server caps it and
 * says when it capped, because a queue long enough to truncate is an alarm
 * about review having stopped, not a paging problem.
 *
 * ## Two things deliberately absent
 *
 * - **No bulk approve.** At volume it is tempting and it turns review into a
 *   rubber stamp with an audit trail claiming a human looked. The honest answer
 *   to throughput is a visible, revocable trusted-supplier flag, not a button
 *   that approves forty things nobody opened.
 * - **No second permission key.** Reviewing is `canManageDistribution`, which
 *   already gates the whole catalogue including reads. A reviewer is by
 *   definition someone allowed to see every supplier's material.
 */

type Decision = "approve" | "reject" | "request-changes";

/**
 * What the Material column says, and it is answering "is anything happening to
 * this file?" — not "is it watchable?".
 *
 * Three answers, because there are three situations and the old two-way label
 * merged the two that matter most on this page:
 *
 *  - a rendition exists                -> the reviewer can press play;
 *  - held at `awaiting_review`         -> NOTHING is happening, and the person
 *                                         reading this row is the reason;
 *  - held at `incomplete`              -> nothing is happening, and the
 *                                         SUPPLIER is the one still to act;
 *  - no hold and no rendition          -> it really is being worked on.
 *
 * The order matters: playable wins, because a job can be watchable and still
 * carry a stale hold, and "you can watch it" is the answer the reviewer needs.
 */
export function materialLabel(row: SubmissionRow): string {
  if (row.playable) return "Ready to watch";
  if (row.holdReason === "awaiting_review") return "Held for your review";
  if (row.holdReason === "incomplete") return "Waiting on the supplier";
  return "Still landing";
}

const STATE_LABEL: Record<string, string> = {
  submitted: "Waiting",
  changes_requested: "Changes asked for",
  approved: "Approved",
  rejected: "Rejected",
  draft: "Draft",
};

export default function SubmissionsPage() {
  /**
   * VOD / Feed - two entirely separate questions sharing one page.
   *
   * VOD is what this page has always been: screening a supplier's own
   * upload. Feed is the newer one - is there a title in a provider's MRSS
   * export that Foyer does not have yet - and reuses none of the VOD queue's
   * state, same pattern already shipped for `ChannelsPage.tsx`'s Live/Effect
   * toggle: two reads over different data, one page, a plain toggle between
   * them, no third state to keep in sync.
   */
  const [kind, setKind] = useState<"vod" | "feed">("vod");

  const [queue, setQueue] = useState<SubmissionQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<SubmissionRow | null>(null);

  const [supplier, setSupplier] = useState("");
  const [state, setState] = useState("");
  const [type, setType] = useState("");
  const [language, setLanguage] = useState("");

  async function refresh() {
    try {
      setQueue(await foyerApi.listSubmissions());
      setError(null);
    } catch (e) {
      setError(friendlyError(e));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // Memoised so the facet lists below are not rebuilt on every keystroke in a
  // filter - `queue?.submissions ?? []` is a fresh array each render.
  const rows = useMemo(() => queue?.submissions ?? [], [queue]);

  /** Facet values taken from the loaded SET, so an empty option cannot exist. */
  const facets = useMemo(
    () => ({
      suppliers: [...new Set(rows.map((r) => r.ownerName))].sort(),
      states: [...new Set(rows.map((r) => r.state))].sort(),
      types: [...new Set(rows.map((r) => r.type).filter(Boolean))].sort(),
      languages: [...new Set(rows.flatMap((r) => r.languages))].sort(),
    }),
    [rows]
  );

  const filtered = rows.filter(
    (row) =>
      (!supplier || row.ownerName === supplier) &&
      (!state || row.state === state) &&
      (!type || row.type === type) &&
      (!language || row.languages.includes(language))
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Distribution"
        title="Review queue"
        subtitle={
          kind === "vod"
            ? "Screen a supplier's upload before it reaches a guest. Nothing here is in a package yet, so nothing here can be seen or played."
            : "See what a provider's own feed carries that Foyer does not have yet, and turn it into a real title."
        }
      />

      {/* 🚨 Sigge: "disappearingly small" - the whole page below it is
          either a screening queue or a feed diff, so this decides what the
          reviewer is even looking at. Sized and iconed to read as the
          page's main switch, not a filter chip. */}
      <ToggleButtonGroup
        exclusive
        value={kind}
        onChange={(_e, next) => next && setKind(next)}
        sx={{ mb: 3 }}
        data-testid="submissions-kind-toggle"
      >
        <ToggleButton
          value="vod"
          data-testid="submissions-kind-vod"
          sx={{
            gap: 1,
            px: 3,
            py: 1.25,
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          <OndemandVideoIcon fontSize="small" />
          VOD
        </ToggleButton>
        <ToggleButton
          value="feed"
          data-testid="submissions-kind-feed"
          sx={{
            gap: 1,
            px: 3,
            py: 1.25,
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          <RssFeedIcon fontSize="small" />
          Feed
        </ToggleButton>
      </ToggleButtonGroup>

      {kind === "feed" ? (
        <FeedReviewView />
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {queue?.truncated && (
            // Stated rather than hidden: a queue this long means review has stopped
            // happening, and the answer is people or a trusted-supplier flag.
            <Alert severity="warning" sx={{ mb: 2 }}>
              Showing the {rows.length} oldest of {queue.total} open cases. A
              queue this long is worth acting on rather than scrolling.
            </Alert>
          )}

          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Filter
                label="Supplier"
                value={supplier}
                options={facets.suppliers}
                onChange={setSupplier}
              />
              <Filter
                label="Status"
                value={state}
                options={facets.states}
                render={(v) => STATE_LABEL[v] ?? v}
                onChange={setState}
              />
              <Filter
                label="Type"
                value={type}
                options={facets.types as string[]}
                onChange={setType}
              />
              <Filter
                label="Language"
                value={language}
                options={facets.languages}
                onChange={setLanguage}
              />
            </Stack>
          </Card>

          <Card sx={{ overflow: "hidden" }}>
            {!queue ? (
              <TableSkeleton rows={5} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell sx={{ width: 180 }}>Supplier</TableCell>
                      <TableCell sx={{ width: 120 }}>Type</TableCell>
                      <TableCell sx={{ width: 160 }}>Status</TableCell>
                      <TableCell sx={{ width: 150 }}>Material</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box sx={{ py: 5, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              {rows.length === 0
                                ? "Nothing waiting for review."
                                : "No case matches those filters."}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {filtered.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setOpen(row)}
                        data-testid={`submission-${row.assetId}`}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.reference ?? row.assetId}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.ownerName}</TableCell>
                        <TableCell>{row.type ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={STATE_LABEL[row.state] ?? row.state}
                            color={
                              row.state === "changes_requested"
                                ? "warning"
                                : "default"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {/* The two questions kept apart: metadata is always
                          reviewable, the MATERIAL only once a rendition
                          exists. Conflating them puts a dead player in front
                          of a reviewer.

                          ⚠️ But "no rendition yet" is TWO situations, and this
                          column used to call both of them "Still landing".
                          For a held job that is backwards: the gate parks an
                          untrusted supplier's master at `awaiting_review` and
                          the dispatcher skips it, so nothing is encoding and
                          nothing will until the person reading this row
                          approves it. The label promised progress that the
                          page itself was blocking — and it is the common case
                          here, because a queue of things awaiting review is
                          what this page IS.

                          The sentence comes from `ingestHoldExplanation`,
                          shared with the supplier shell so a hold is never
                          described two ways. */}
                          <Tooltip
                            title={
                              row.holdReason
                                ? ingestHoldExplanation(row.holdReason)
                                : ""
                            }
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                row.playable
                                  ? "success"
                                  : row.holdReason === "awaiting_review"
                                    ? "warning"
                                    : "default"
                              }
                              label={materialLabel(row)}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>

          {open && (
            // Remounted by key rather than syncing props: a prop-watching reset
            // cannot tell "different case" from "same case reopened", which is how
            // a drawer saves the previous row's decision.
            <ReviewDrawer
              key={open.id}
              row={open}
              packages={queue?.packages ?? []}
              onClose={() => setOpen(null)}
              onDone={() => {
                setOpen(null);
                refresh();
              }}
              onError={setError}
            />
          )}
        </>
      )}
    </PageContainer>
  );
}

function Filter({
  label,
  value,
  options,
  onChange,
  render,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
  render?: (value: string) => string;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 170 }}
    >
      <MenuItem value="">
        <em>Any</em>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {render ? render(option) : option}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * The raw master, so a reviewer can actually watch what they are approving.
 *
 * 🚨 Until this existed, screening was metadata only. The queue said "Ready to
 * watch" and there was nothing to watch — an approval nobody could base on the
 * file it approved, which is a rubber stamp with a queue attached. And under
 * the ingest gate it matters more, not less: an untrusted supplier's material
 * is now HELD before transcoding precisely so a person sees it first.
 *
 * ⚠️ The src is a SAS URL, which is a CREDENTIAL. It is minted per opening,
 * expires in minutes, and is deliberately not kept anywhere — no state that
 * outlives the drawer, no logging, and re-opening mints a fresh one rather than
 * reusing a stale one.
 */
function RawPreview({
  assetId,
  onError,
}: {
  assetId: string;
  onError: (message: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "none" | "ready">("loading");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { jobs } = await foyerApi.listIngestJobs(assetId);
        // The live one. A finished ingest has had its master consumed, and an
        // older failed attempt is not what is being reviewed.
        const job = jobs.find((j) => j.phase === "queued") ?? jobs[0];
        if (!job) {
          if (live) setState("none");
          return;
        }
        const preview = await foyerApi.ingestPreview(job.id);
        if (!live) return;
        setUrl(preview.url);
        setState("ready");
      } catch (e) {
        if (!live) return;
        const err = e as { code?: string };
        // "No bytes yet" is not a failure worth an error banner — it is the
        // normal state of a submission whose upload has not finished.
        if (err.code === "no_bytes_uploaded" || err.code === "not_found") {
          setState("none");
          return;
        }
        setState("none");
        onError(friendlyError(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [assetId, onError]);

  if (state === "loading") {
    return <Typography variant="body2">Fetching the material…</Typography>;
  }
  if (state === "none" || !url) {
    return (
      <Alert severity="info" data-testid="review-no-material">
        No uploaded master to watch yet. The metadata can be reviewed now.
      </Alert>
    );
  }
  return (
    <Box>
      <video
        src={url}
        controls
        preload="metadata"
        style={{ width: "100%", borderRadius: 8, background: "#000" }}
        data-testid="review-player"
      />
      <Typography variant="caption" color="text.secondary">
        The uploaded master, exactly as delivered. Nothing has been encoded yet.
      </Typography>
    </Box>
  );
}

/**
 * The catalogue's own id for this title, with a way to take it away.
 *
 * ## Why it is shown at all
 *
 * The line above reads `Tigrar · movie · NENDA-100250`. That `NENDA-` number is
 * FOYER's reference, minted by us; the Content API knows this title only as
 * `6780a81d-…`. Every upstream conversation — a catalogue query, a thread with
 * the CMS team, a row in somebody else's report — is keyed on the uuid, and
 * until now the only way to get it was the address bar or a database query.
 *
 * ⚠️ Failure is SHOWN, not swallowed. `navigator.clipboard` is unavailable in
 * an insecure context and can be refused outright, and the neighbouring copy
 * buttons in this repo `.catch(() => {})` — so the button appears to work and
 * the operator pastes whatever was on the clipboard before. For an id that is
 * about to go into a support ticket, a silent no-op is worse than an error: it
 * is a wrong id delivered with confidence. The text stays selectable either
 * way, so the manual route is always there.
 */
export function CopyableId({ id }: { id: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setState("copied");
    } catch {
      setState("failed");
    }
    setTimeout(() => setState("idle"), 2000);
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          userSelect: "all",
        }}
        data-testid="submission-asset-id"
      >
        {id}
      </Typography>
      <Tooltip
        title={
          state === "copied"
            ? "Copied"
            : state === "failed"
              ? "Could not copy — select the id and copy it manually"
              : "Copy the catalogue id"
        }
      >
        <IconButton
          aria-label="Copy the catalogue id"
          onClick={() => void copy()}
          data-testid="submission-copy-id"
          // The state, readable without asserting on a colour — the palette
          // has a light and a dark `error` and the test should not care which.
          data-state={state}
          sx={{
            p: 0.25,
            // The brand blue, as everywhere else. Red ONLY on a refusal — the
            // one state the operator must not read past.
            color: state === "failed" ? tokens.error : tokens.brand,
            "&:hover": { background: tokens.brandTintSoft },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function ReviewDrawer({
  row,
  packages,
  onClose,
  onDone,
  onError,
}: {
  row: SubmissionRow;
  packages: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  // Seeded ONCE from the row. The component is remounted by key when the case
  // changes, so there is nothing to re-sync.
  const [note, setNote] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  /**
   * The whole title, because a review is a judgement about the whole title.
   *
   * The queue row carries what a LIST needs. Deciding whether something can be
   * offered to a customer needs what the editor sees — images, localisation,
   * licence windows, packages, ingest phase — so the drawer reads the title in
   * full, exactly as `cms-list-and-editor` says a drawer must: *"a row must
   * carry everything its editor edits ... a half-loaded case is a decision made
   * about a title nobody saw."*
   */
  const [full, setFull] = useState<VodTitleRow | null>(null);
  const [jobs, setJobs] = useState<IngestJobView[] | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  const reload = useCallback(async () => {
    const title = await foyerApi.getVodTitle(row.assetId);
    setFull(title);
  }, [row.assetId]);

  useEffect(() => {
    let live = true;
    void reload().catch((e) => live && onError(friendlyError(e)));
    foyerApi
      .listIngestJobs(row.assetId)
      .then((answer) => live && setJobs(answer.jobs))
      // A failure must not read as "no ingest": the overview draws a phase
      // from this, and "I do not know" and "nothing" are opposite facts.
      .catch(() => live && setJobs(null));
    foyerApi
      .listContentProviders()
      .then((p) => live && setProviders(p))
      .catch(() => {});
    foyerApi
      .listContentGenres()
      .then((g) => live && setGenres(g))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [row.assetId, reload, onError]);

  /**
   * The same two derivations the editor's own rail draws, from the same shared
   * functions. Recomputed here rather than shipped on the row for the reason
   * `AssetOverviewTab` gives: they are pure functions of the asset, and a
   * second copy on the wire is a second thing to keep in step.
   */
  const blockers = useMemo(
    () => (full ? assetPlaybackBlockers(full, ["main"]) : []),
    [full]
  );
  const availability = useMemo(() => availabilityFor(full ?? {}), [full]);

  async function decide(decision: Decision) {
    setBusy(true);
    try {
      await foyerApi.reviewSubmission(row.assetId, {
        decision,
        note,
        ...(decision === "approve" ? { packages: chosen } : {}),
      });
      onDone();
    } catch (e) {
      onError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const needsNote = note.trim() === "";

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{row.title}</DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
          sx={{ mt: 1 }}
        >
          {/**
           * ⚠️ The OVERVIEW leads, not the video.
           *
           * This drawer used to be `maxWidth="sm"` with the player first,
           * because "what is being screened is the material". That is true and
           * it is not the whole question: the decision is *can this be offered
           * to a customer*, and a reviewer answering it needs packages,
           * artwork, localisation, licence window and ingest phase — none of
           * which a video player shows. Watching the film told them the one
           * thing they could already see.
           *
           * It is `AssetOverviewTab`, the same component the editor's Översikt
           * draws, NOT a reviewer's checklist. A second list of "is this
           * ready?" findings would drift from the editor's within a release,
           * and the operator would then have two screens disagreeing about one
           * title with no way to tell which is right.
           */}
          <Box sx={{ flex: "1 1 58%", minWidth: 0, width: "100%" }}>
            {full ? (
              <AssetOverviewTab
                assetId={row.assetId}
                asset={full}
                blockers={blockers}
                availability={availability}
                jobs={jobs}
                /**
                 * The package choice lives ON the Packages row now, behind its
                 * caret, rather than as a field further down the drawer.
                 *
                 * That row is where the problem is stated — "In none of N
                 * packages — no guest can see this title" — and the answer was
                 * two panels away, which is how a reviewer approves a title
                 * into nothing. It is the SAME `chosen` state the approval
                 * sends; nothing new writes anything.
                 */
                packagePicker={{
                  options: packages,
                  value: chosen,
                  onChange: setChosen,
                }}
              />
            ) : (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={20} />
              </Stack>
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={!full}
                onClick={() => setEditing(true)}
                data-testid="review-edit"
              >
                Edit fields
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                A gap a reviewer can close in ten seconds should not cost the
                supplier a round trip.
              </Typography>
            </Box>
          </Box>

          <Stack
            spacing={2}
            sx={{ flex: "1 1 42%", minWidth: 0, width: "100%" }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                {row.ownerName} · {row.type ?? "unknown type"} ·{" "}
                {/* Foyer's OWN reference. Showing the uuid in its place when
                    absent made the two ids look like one thing with two
                    spellings — the confusion the row below exists to end. */}
                {row.reference ?? "no reference yet"}
              </Typography>
              <CopyableId id={row.assetId} />
            </Box>

            {/**
             * Still here, and still the thing being screened — just no longer the
             * only thing the drawer says.
             */}
            <RawPreview assetId={row.assetId} onError={onError} />

            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              helperText="Required when rejecting or asking for changes. A refusal with no reason generates a phone call."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                History
              </Typography>
              {/* The record, not just the head: "rejected twice, approved on the
                third" is a fact about a supplier worth seeing before deciding
                a fourth time. */}
              <Stack spacing={0.25}>
                {row.history.map((event, i) => (
                  <Typography
                    key={`${event.at}-${i}`}
                    variant="caption"
                    color="text.secondary"
                  >
                    {event.at.slice(0, 16).replace("T", " ")} ·{" "}
                    {STATE_LABEL[event.state] ?? event.state}
                    {event.by ? ` · ${event.by}` : ""}
                    {event.note ? ` — ${event.note}` : ""}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          color="warning"
          disabled={busy || needsNote}
          onClick={() => decide("request-changes")}
        >
          Ask for changes
        </Button>
        <Button
          color="error"
          disabled={busy || needsNote}
          onClick={() => decide("reject")}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => decide("approve")}
          data-testid="submission-approve"
        >
          Approve
        </Button>
      </DialogActions>

      {/**
       * ⚠️ The EXISTING editor, not a reviewer's copy of it.
       *
       * A reviewer who can see a gap should be able to close it — "no synopsis
       * in English" is ten seconds of typing and otherwise costs the supplier a
       * rejection, a mail, and another trip through the wizard. But the fields
       * are the same fields, and a second editor beside the first is how two
       * screens start writing different payloads for the same title. So this
       * is `AssetEditorDialog`, saving through `draftToUpdate` exactly as the
       * Titles page does — the payload shaper stays single.
       */}
      {editing && full && (
        <AssetEditorDialog
          key={full.assetId}
          open
          kind="title"
          initial={full}
          providers={providers}
          genres={genres}
          saving={saving}
          error={saveError}
          onClose={() => {
            setEditing(false);
            setSaveError(null);
          }}
          onSubmit={async (draft, options) => {
            setSaving(true);
            setSaveError(null);
            try {
              await foyerApi.updateVodTitle(
                full.assetId,
                draftToUpdate(draft, full, options)
              );
              // Re-read rather than patch locally: the overview beside this is
              // derived from the title, and a local guess about what the row
              // now holds is how a summary starts lying.
              await reload();
              setEditing(false);
            } catch (e) {
              const err = e as { code?: string; message: string };
              setSaveError({ code: err.code, message: err.message });
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
    </Dialog>
  );
}
